import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  AssignableUser,
  GetAccessibleSupportUsersResponse,
} from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import { supportTicketHydrationQueryRetryOptions } from "@redotech/redo-support-v2/query/support-ticket-hydration-query";
import { supportTicketUserQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-users-collection";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catchApiErrorsSilent } from "../db/error-handling";

const EMPTY_USERS: readonly AssignableUser[] = [];
const FIVE_MINUTES_MS = 5 * 60 * 1000;

export interface UseAccessibleSupportUsersResult {
  users: readonly AssignableUser[];
  /** No data yet and a first fetch is in flight. */
  isLoading: boolean;
  /** A fetch (initial or background) is currently running. */
  isFetching: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

/** Single React Query cache entry for accessible support users, read by every assignee surface. */
export function useAccessibleSupportUsers(): UseAccessibleSupportUsersResult {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const scope = useSupportTicketQueryScope();

  const query = useQuery<GetAccessibleSupportUsersResponse>({
    queryKey: supportTicketUserQueryKeys.accessibleUsers(scope),
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrorsSilent(
          client.supportTicketRelatedRecords.getAccessibleSupportUsers({}),
        ),
      );
      return { users: [...result.users] };
    },
    staleTime: FIVE_MINUTES_MS,
    retry: supportTicketHydrationQueryRetryOptions.retry,
    retryDelay: supportTicketHydrationQueryRetryOptions.retryDelay,
    refetchInterval: supportTicketHydrationQueryRetryOptions.refetchInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: { showErrorToast: true },
  });

  return {
    users: query.data?.users ?? EMPTY_USERS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** `MongoUserId → AssignableUser` for surfaces that only receive a user id. */
export function useAccessibleSupportUserMap(): Map<
  MongoUserId,
  AssignableUser
> {
  const { users } = useAccessibleSupportUsers();
  return useMemo(() => {
    const map = new Map<MongoUserId, AssignableUser>();
    for (const user of users) {
      map.set(user._id, user);
    }
    return map;
  }, [users]);
}
